import moment from 'moment/moment'
import React, { useState, useEffect } from 'react'
import SmartLink from '@/component/Primitive/SmartLink'
import Type from '@/component/Primitive/Type'
import cx from 'classnames'
import Icon from '@/component/Primitive/Icon'
import propTypes from 'prop-types'
import styles from './EventSidebar.module.scss'
import ButtonStandard from '@/component/Primitive/ButtonStandard'

const LINK_TYPES = {
  LABEL: 'label',
  EVENT_SECTION: 'eventSection',
  EVENT_UMBRELLA_SECTION: 'eventUmbrellaSection',
  EXTERNAL_LINK: 'externalLink'
}

const buildUrl = (link, baseUrl) => {
  const { type, url } = link
  let builtUrl

  switch (type) {
    case LINK_TYPES.LABEL:
      builtUrl = null
      break
    case LINK_TYPES.EVENT_SECTION:
    case LINK_TYPES.EVENT_UMBRELLA_SECTION:
      builtUrl = url ? `${baseUrl}${url}` : baseUrl
      break
    case LINK_TYPES.EXTERNAL_LINK:
      builtUrl = url
      break
    default:
      builtUrl = null
      break
  }

  return builtUrl?.replace(/\/$/, '') || builtUrl
}

const buildCtaUrl = (cta, baseUrl) => {
  if (cta.eventLayoutSlug) {
    return `${baseUrl}/${cta.eventLayoutSlug}`
  }
  return cta.link || ''
}

const buildLogoUrl = (logoAction, proxyEventHomeUrl, baseUrl) => {
  if (logoAction?.eventLayoutSlug) {
    return `${baseUrl}/${logoAction.eventLayoutSlug}`
  }
  if (logoAction?.link) {
    return logoAction.link
  }
  return proxyEventHomeUrl || baseUrl
}

const getExternalLinkProps = (item) => {
  if (item.type === LINK_TYPES.EXTERNAL_LINK) {
    return {
      href: item.url,
      target: '_blank',
      rel: 'noopener noreferrer'
    }
  }
  return {}
}

const CollapsibleNavigationItem = ({
  item,
  index,
  baseUrl,
  activeMenuIndex,
  setActiveMenuIndex,
  currentUrl
}) => {
  const hasSubItems = item.subItems?.length > 0
  const isActive = activeMenuIndex === index
  const itemUrl = buildUrl(item, baseUrl)
  const isCurrentPage = currentUrl === itemUrl

  const hasActiveSubItem =
    hasSubItems &&
    item.subItems.some((subItem) => currentUrl === buildUrl(subItem, baseUrl))

  useEffect(() => {
    if ((isCurrentPage || hasActiveSubItem) && hasSubItems) {
      setActiveMenuIndex(index)
    }
  }, [
    currentUrl,
    isCurrentPage,
    hasActiveSubItem,
    hasSubItems,
    index,
    setActiveMenuIndex
  ])

  const handleToggle = () => {
    if (hasSubItems) {
      setActiveMenuIndex(isActive ? null : index)
    }
  }

  const handleMainLinkClick = (e) => {
    if (hasSubItems) {
      e.preventDefault()
      handleToggle()
    }
  }

  const renderSubMenu = () => {
    if (!hasSubItems) return null

    return (
      <div className={cx(styles.SubMenu, isActive && styles.open)}>
        <ul className={cx(styles.SubMenuContent, styles.List)}>
          {item.subItems.map((subItem, subIndex) => {
            const subItemUrl = buildUrl(subItem, baseUrl)
            const isSubItemActive = currentUrl === subItemUrl

            return (
              <li key={`sub-${index}-${subIndex}`}>
                <SmartLink
                  to={subItem.type.includes('event') ? '/events' : ''}
                  as={subItemUrl}
                  className={cx(
                    styles.SubMenuItem,
                    isSubItemActive && styles.currentPage
                  )}
                  {...getExternalLinkProps(subItem)}
                >
                  {subItem.title}
                </SmartLink>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  return (
    <li className={cx(styles.CollapsibleNavItem, isActive && styles.active)}>
      <div className={styles.MainNavItem}>
        <SmartLink
          to={item.type.includes('event') ? '/events' : ''}
          as={itemUrl}
          className={cx(
            styles.EventNavigationItemLink,
            isCurrentPage && styles.currentPage,
            hasSubItems && styles.hasSubItems
          )}
          onClick={handleMainLinkClick}
          {...getExternalLinkProps(item)}
        >
          <span className={styles.NavItemText}>{item.title}</span>
          {hasSubItems && (
            <Icon
              type="arrow-down"
              className={cx(styles.ToggleIcon, isActive && styles.rotated)}
              width={12}
              height={7}
            />
          )}
        </SmartLink>
      </div>
      {renderSubMenu()}
    </li>
  )
}

CollapsibleNavigationItem.propTypes = {
  item: propTypes.shape({
    title: propTypes.string,
    type: propTypes.string,
    url: propTypes.string,
    subItems: propTypes.array
  }),
  index: propTypes.number,
  baseUrl: propTypes.string,
  activeMenuIndex: propTypes.number,
  setActiveMenuIndex: propTypes.func,
  currentUrl: propTypes.string
}

const NavigationLogo = ({ logoUrl, logoAction, light, isEventPage }) => (
  <SmartLink
    as={logoUrl}
    to="/events"
    {...(logoAction?.openInNewTab && {
      target: '_blank',
      rel: 'noopener noreferrer'
    })}
  >
    <img alt="" src={light} />
  </SmartLink>
)

NavigationLogo.propTypes = {
  logoUrl: propTypes.string,
  logoAction: propTypes.shape({
    eventLayoutSlug: propTypes.string,
    link: propTypes.string,
    openInNewTab: propTypes.bool
  }),
  light: propTypes.string,
  isEventPage: propTypes.bool
}

const BackLink = ({ eventUmbrellaUrl }) => (
  <SmartLink as={eventUmbrellaUrl} to="/events">
    <Type as="p" weight={['bold', 'medium']} size={['body4', 'body3']}>
      <Icon
        width={9}
        height={12}
        type="arrow-left"
        a11yText="Back to event Portfolio"
      />
      Back to Portfolio
    </Type>
  </SmartLink>
)

BackLink.propTypes = {
  eventUmbrellaUrl: propTypes.string
}

const EventNavigation = ({
  baseUrl,
  baseUrls,
  eventNavItems,
  logos,
  isEventPage,
  showLogoInNavigation,
  ctaButton,
  awardsCtaButton,
  proxyEventHomeUrl,
  logoAction
}) => {
  const [activeMenuIndex, setActiveMenuIndex] = useState(0)
  const [currentUrl, setCurrentUrl] = useState('')

  useEffect(() => {
    setCurrentUrl(window.location.pathname)

    for (const index in eventNavItems) {
      if (eventNavItems[index].subItems.length) {
        setActiveMenuIndex(parseInt(index))
        break
      }
    }
  }, [])

  const logoUrl = buildLogoUrl(logoAction, proxyEventHomeUrl, baseUrl)

  const renderNavigation = () => {
    if (!isEventPage && showLogoInNavigation) {
      return (
        <NavigationLogo
          logoUrl={logoUrl}
          logoAction={logoAction}
          light={logos.light}
          isEventPage={isEventPage}
        />
      )
    }

    if (isEventPage && !showLogoInNavigation) {
      return <BackLink eventUmbrellaUrl={baseUrls.eventUmbrella} />
    }

    if (isEventPage && showLogoInNavigation) {
      return (
        <NavigationLogo
          logoUrl={logoUrl}
          logoAction={logoAction}
          light={logos.light}
          isEventPage={isEventPage}
        />
      )
    }

    return null
  }

  const renderCtaButton = (button, isAwards) => {
    if (!button) return null

    return (
      <ButtonStandard
        inverse
        size="small"
        icon="arrow-right"
        to={button.eventLayoutSlug ? '/events' : ''}
        as={button.eventLayoutSlug ? buildCtaUrl(button, baseUrl) : ''}
        className={cx(
          styles.CtaButton,
          isAwards && styles.CtaButtonAwards,
          button.buttonClassName
        )}
        {...(button.link && {
          href: button.link,
          target: button.openInNewTab ? '_blank' : undefined,
          rel: 'noopener noreferrer'
        })}
      >
        {button.label}
      </ButtonStandard>
    )
  }

  return (
    <>
      {renderNavigation()}
      <nav
        className={cx(styles.EventNavigationList, isEventPage && styles.event)}
      >
        {eventNavItems && (
          <ul className={styles.List}>
            {eventNavItems.map((item, index) => {
              return (
                <CollapsibleNavigationItem
                  key={`event-${index}`}
                  item={item}
                  index={index}
                  baseUrl={baseUrl}
                  activeMenuIndex={activeMenuIndex}
                  setActiveMenuIndex={setActiveMenuIndex}
                  currentUrl={currentUrl}
                />
              )
            })}
          </ul>
        )}
        {renderCtaButton(awardsCtaButton, true)}
        {renderCtaButton(ctaButton)}
      </nav>
    </>
  )
}

EventNavigation.propTypes = {
  baseUrl: propTypes.string,
  eventNavItems: propTypes.array,
  logos: propTypes.shape({
    light: propTypes.string,
    dark: propTypes.string
  }),
  isEventPage: propTypes.bool,
  baseUrls: propTypes.shape({
    eventUmbrella: propTypes.string,
    event: propTypes.string
  }),
  showLogoInNavigation: propTypes.bool,
  ctaButton: propTypes.shape({
    label: propTypes.string,
    buttonId: propTypes.string,
    buttonClassName: propTypes.string,
    link: propTypes.string,
    eventLayoutSlug: propTypes.string,
    openInNewTab: propTypes.bool
  }),
  awardsCtaButton: propTypes.shape({
    label: propTypes.string,
    buttonId: propTypes.string,
    buttonClassName: propTypes.string,
    link: propTypes.string,
    eventLayoutSlug: propTypes.string,
    openInNewTab: propTypes.bool
  }),
  proxyEventHomeUrl: propTypes.string,
  logoAction: propTypes.shape({
    eventLayoutSlug: propTypes.string,
    link: propTypes.string,
    openInNewTab: propTypes.bool
  })
}

const buildCtaButton = (eventUmbrella, isEventPage) => {
  if (!isEventPage) return null

  const specialActions = eventUmbrella?.event?.specialActions
  const navigationCta = specialActions?.navigation_cta
  const fallbackCta = eventUmbrella?.event?.navigationCtaButton

  const cta = navigationCta || fallbackCta
  if (!cta) return null

  return {
    label: cta.labelOverride || cta.label,
    buttonId: cta.buttonId,
    buttonClassName: cta.buttonClassName,
    eventLayoutSlug: cta.eventLayoutSlug,
    link: cta.link,
    openInNewTab: cta.openInNewTab
  }
}

const buildAwardsCtaButton = (eventUmbrella, isEventPage) => {
  if (!isEventPage) return null

  const cta = eventUmbrella?.event?.awardsNavigationCtaButton
  if (!cta) {
    return null
  }

  return {
    label: cta.labelOverride || cta.label,
    buttonId: cta.buttonId,
    buttonClassName: cta.buttonClassName,
    eventLayoutSlug: cta.eventLayoutSlug,
    link: cta.link,
    openInNewTab: cta.openInNewTab
  }
}

const buildLogoAction = (eventUmbrella, isEventPage) => {
  if (!isEventPage) return null
  return eventUmbrella?.event?.specialActions?.navigation_logo || null
}

const buildLogos = (eventUmbrella, isEventPage) => {
  if (isEventPage) {
    return {
      light: eventUmbrella?.event?.lightLogoUrl,
      dark: eventUmbrella?.event?.darkLogoUrl
    }
  }
  return {
    light: eventUmbrella?.lightLogoUrl,
    dark: eventUmbrella?.darkLogoUrl
  }
}

const buildLiveAction = (eventUmbrella, isEventPage) => {
  if (!isEventPage) return null

  const specialActions = eventUmbrella?.event?.specialActions
  return specialActions?.live || eventUmbrella?.event?._liveAction || null
}

const EventSidebar = (props) => {
  const { eventUmbrella, baseUrls } = props

  const now = moment()
  const startDate = moment(eventUmbrella?.event?.startDate)
  const endDate = moment(eventUmbrella?.event?.endDate)
  const isEventLive = now.isBetween(startDate, endDate)

  const isEventPage = !!baseUrls?.event
  const showLogoInNavigation = isEventPage
    ? eventUmbrella?.event?.showLogoInNavigation
    : eventUmbrella?.showLogoInNavigation

  const eventNavigationProps = {
    inverse: false,
    ctaButton: buildCtaButton(eventUmbrella, isEventPage),
    awardsCtaButton: buildAwardsCtaButton(eventUmbrella, isEventPage),
    logoAction: buildLogoAction(eventUmbrella, isEventPage),
    eventNavItems: isEventPage
      ? eventUmbrella?.event?.navItems
      : eventUmbrella?.navItems,
    baseUrl: isEventPage ? baseUrls?.event : baseUrls?.eventUmbrella,
    baseUrls,
    logos: buildLogos(eventUmbrella, isEventPage),
    isEventPage,
    isEventLive,
    showLogoInNavigation,
    proxyEventHomeUrl: isEventPage
      ? eventUmbrella?.event?.proxyEventHomeUrl
      : null,
    liveAction: buildLiveAction(eventUmbrella, isEventPage)
  }

  return (
    <div className={styles.Sidebar}>
      <div className={styles.SidebarInner}>
        <EventNavigation {...eventNavigationProps} />
      </div>
    </div>
  )
}

EventSidebar.propTypes = {
  eventUmbrella: propTypes.object,
  baseUrls: propTypes.shape({
    eventUmbrella: propTypes.string,
    event: propTypes.string
  })
}

export default EventSidebar
